/**
 * DEPRECATED: Layout is now defined in docViewSchemas/item.ts for this doctype.
 * 
 * This file is kept for reference during migration. Do not use for new features.
 * The canonical layout source for Item is: src/lib/views/docViewSchemas/item.ts
 * 
 * ---
 * 
 * DEC-001: Shopify-style Experience View for Item DocType
 * 
 * This view creates a Shopify-like item detail page with a Details tab.
 * 
 * Details Tab Sections:
 * - item-gallery: Product images gallery (Shopify-style upload interface)
 * - item-overview: Item identification and key information
 * - item-details: Core item details (description, type, group, etc.)
 * - pricing: Pricing and valuation information
 * - inventory: Stock and warehouse information
 * - status-summary: Item status and availability
 */

import type { DocTypeView } from '../types';

export const itemShopifyView: DocTypeView = {
  id: 'item-shopify-v1',
  doctype: 'Item',
  style: 'shopify',
  context: undefined,
  status: 'active',
  label: 'Item – Shopify Style',
  description: 'Shopify-style layout for Item detail page with Details tab',
  formView: {
    sections: [
      // 0. Item Gallery (Shopify-style image gallery)
      {
        id: 'item-gallery',
        title: 'Product Images',
        description: 'Upload and manage product images',
        layout: [
          {
            columns: [
              {
                fieldname: 'item_gallery',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
                // Special marker for Shopify-style gallery rendering (not table)
                // @ts-expect-error - _renderAs is a custom extension
                _renderAs: 'shopify-gallery',
              },
            ],
          },
        ],
      },

      // 1. Item Overview (header info)
      {
        id: 'item-overview',
        title: 'Item Overview',
        description: 'Item identification and key status information',
        layout: [
          {
            columns: [
              {
                fieldname: 'item_code',
                mode: 'read',
                visibility: 'default',
                width: '2/3',
              },
              {
                fieldname: 'disabled',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'item_name',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'brand',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'packing_unit',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'description',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'item_group',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'stock_uom',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 2. Item Details (core information)
      {
        id: 'item-details',
        title: 'Item Details',
        description: 'Core item information and classification',
        layout: [
          {
            columns: [
              {
                fieldname: 'item_type',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'has_variants',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'manufacturer',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'manufacturer_part_no',
                mode: 'edit',
                visibility: 'advanced',
                width: '1/3',
              },
            ],
          },
        ],
      },

      // 3. Pricing (pricing and valuation)
      {
        id: 'pricing',
        title: 'Pricing',
        description: 'Pricing and valuation information',
        layout: [
          {
            columns: [
              {
                fieldname: 'standard_rate',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'valuation_rate',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'last_purchase_rate',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'base_unit_rate',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'is_purchase_item',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
        ],
      },

      // 4. Inventory (stock and warehouse)
      {
        id: 'inventory',
        title: 'Inventory',
        description: 'Stock and warehouse information',
        layout: [
          {
            columns: [
              {
                fieldname: 'is_stock_item',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'include_item_in_manufacturing',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'is_fixed_asset',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'default_warehouse',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'safety_stock',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 5. Status Summary
      {
        id: 'status-summary',
        title: 'Status Summary',
        description: 'Item status and availability information',
        layout: [
          {
            columns: [
              {
                fieldname: 'disabled',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'end_of_life',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 6. Inventory (dynamically populated from SystemView)
      {
        id: 'inventory-tab',
        title: 'Inventory',
        description: 'Inventory and stock management fields',
        // Special marker: layout will be populated dynamically from SystemView
        layout: [],
        // Metadata to indicate this should be populated from SystemView tab
        _dynamicFromSystemTab: 'inventory',
      },

      // 7. Types (dynamically populated from SystemView)
      {
        id: 'types-tab',
        title: 'Types',
        description: 'Item type and classification fields',
        layout: [],
        _dynamicFromSystemTab: 'types',
      },

      // 8. Variants (dynamically populated from SystemView, conditional visibility)
      {
        id: 'variants-tab',
        title: 'Variants',
        description: 'Item variant configuration',
        layout: [],
        _dynamicFromSystemTab: 'variants',
        // Conditional visibility: show if item has variants (has_variants === 1) 
        // OR is itself a variant (variant_of is set)
        // Special marker - will be checked in ExperienceView
        _conditionalVisibility: {
          field: '__has_variants_or_is_variant__', // Special marker - will be checked in ExperienceView
          value: true,
        },
      },

      // 9. Accounting (dynamically populated from SystemView)
      {
        id: 'accounting-tab',
        title: 'Accounting',
        description: 'Accounting and financial fields',
        layout: [],
        _dynamicFromSystemTab: 'accounting',
      },

      // 10. Purchase (dynamically populated from SystemView)
      {
        id: 'purchase-tab',
        title: 'Purchase',
        description: 'Purchase and procurement fields',
        layout: [],
        _dynamicFromSystemTab: 'purchase',
      },

      // 11. Sales (dynamically populated from SystemView)
      {
        id: 'sales-tab',
        title: 'Sales',
        description: 'Sales and pricing fields',
        layout: [],
        _dynamicFromSystemTab: 'sales',
      },

      // 12. Tax (dynamically populated from SystemView)
      {
        id: 'tax-tab',
        title: 'Tax',
        description: 'Tax configuration fields',
        layout: [],
        _dynamicFromSystemTab: 'tax',
      },

      // 13. Quality (dynamically populated from SystemView)
      {
        id: 'quality-tab',
        title: 'Quality',
        description: 'Quality control fields',
        layout: [],
        _dynamicFromSystemTab: 'quality',
      },

      // 14. Manufacturing (dynamically populated from SystemView)
      {
        id: 'manufacturing-tab',
        title: 'Manufacturing',
        description: 'Manufacturing and production fields',
        layout: [],
        _dynamicFromSystemTab: 'manufacturing',
      },
    ],
    customFieldBucket: {
      enabled: true,
      sectionTitle: 'Additional ERP Fields',
      visibilityDefault: 'collapsed',
    },
  },
  createdBy: 'system',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

